import React from 'react';
import { makeStyles } from 'tss-react/mui';
import ClearIcon from '@mui/icons-material/Clear';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { PlanCover } from '../../../..';
import SelectFormField from '@components/form-fields/SelectFormField';
import TextInputFormField from '@components/form-fields/TextInputFormField';
import CurrencyFormField from '@components/form-fields/CurrencyFormField';
import NumberInputFormField from '@components/form-fields/NumberInputFormField';
import { MAIN_THEME } from '@styles';

const useStyles = makeStyles()(() => ({
  container: {
    boxShadow: `0px 0px 10px ${MAIN_THEME.color.white92}`,
    border: `1px solid ${MAIN_THEME.color.white92}`,
    borderRadius: '4px',
    marginTop: '20px',
    marginBottom: '20px',
  },
  headerRow: {
    display: 'grid',
    gridTemplateColumns: '3fr 2fr 2fr 2fr 40px',
    gridColumnGap: '8px',
    padding: '8px 16px',
    backgroundColor: MAIN_THEME.color.white98,
    borderBottom: `1px solid ${MAIN_THEME.color.white92}`,
    fontFamily: MAIN_THEME.font.medium,
    fontSize: MAIN_THEME.fontSize.rg,
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '3fr 2fr 2fr 2fr 40px',
    gridColumnGap: '8px',
    padding: '8px 16px',
    borderBottom: `1px solid ${MAIN_THEME.color.white98}`,
    alignItems: 'center',
    '&:last-of-type': {
      borderBottom: 'none',
    },
  },
  addBtnContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: '10px',
  },
  addBtn: {
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
  },
  removeBtn: {
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
  },
}));

interface ICoverSplitProps {
  currencySymbol: string;
  rows: PlanCover[];
  lovs: Record<string, Record<string, string>>;
  onUpdateRows: (newRows: PlanCover[]) => void;
}

const CoverSplit: React.FC<ICoverSplitProps> = ({
  currencySymbol,
  rows,
  lovs,
  onUpdateRows,
}) => {
  const { classes } = useStyles();

  const handleAddRow = () => {
    const newRow: PlanCover = {
      planCoverID: '',
      planCoverId: '',
      riskRate: 0,
      sumInsured: 0,
      premium: 0,
    };
    onUpdateRows([...rows, newRow]);
  };

  const handleRemoveRow = (index: number) => {
    const newRows = [...rows];
    newRows.splice(index, 1);
    onUpdateRows(newRows);
  };

  const handleChange = <K extends keyof PlanCover>(
    index: number,
    field: K,
    value: PlanCover[K]
  ) => {
    const newRows = [...rows];
    newRows[index][field] = value;

    if (field === 'riskRate' || field === 'sumInsured') {
      const { riskRate, sumInsured } = newRows[index];
      if (riskRate != null && sumInsured != null) {
        newRows[index].premium = (riskRate * sumInsured) / 1000;
      }
    }

    onUpdateRows(newRows);
  };

  return (
    <div className={classes.container}>
      <div className={classes.headerRow}>
        <span>Cover*</span>
        <span>Rate*</span>
        <span>SumInsured</span>
        <span>Premium</span>
        <span />
      </div>

      {rows.map((row, index) => {
        const usedPlanCoverIDs = rows
          .filter((_, i) => i !== index)
          .map((r) => r.planCoverID)
          .filter(Boolean);

        const availablePlanCovers: Record<string, string> = Object.fromEntries(
          Object.entries(lovs.planCovers).filter(
            ([coverId]) =>
              !usedPlanCoverIDs.includes(coverId) || coverId === row.planCoverID
          )
        );

        return (
          <div key={`cover-${index}`} className={classes.row}>
            <SelectFormField
              name={`planCoverID-${index}`}
              title=""
              placeholder="Cover"
              inputOnly
              hideError
              value={row.planCoverId}
              onChange={(val) =>
                handleChange(index, 'planCoverID', val as string)
              }
              selectOptions={availablePlanCovers}
            />
            <NumberInputFormField
              name={`riskRate-${index}`}
              placeholder="Rate"
              title=""
              inputOnly
              hideError
              value={row.riskRate?.toString() || '0'}
              onChange={(e) =>
                handleChange(index, 'riskRate', parseFloat(e.target.value) || 0)
              }
              maxDecimalPrecision={3}
            />
            <CurrencyFormField
              name={`sumInsured-${index}`}
              placeholder="SumInsured"
              hideError
              inputOnly
              maxDecimalPrecision={3}
              value={row.sumInsured}
              onChange={(e) =>
                handleChange(
                  index,
                  'sumInsured',
                  parseFloat(e.target.value) || 0
                )
              }
              title=""
              currencySymbol={currencySymbol}
            />
            <CurrencyFormField
              name={`premium-${index}`}
              placeholder="Premium"
              hideError
              inputOnly
              maxDecimalPrecision={3}
              value={row.premium}
              onChange={(e) =>
                handleChange(index, 'premium', parseFloat(e.target.value) || 0)
              }
              title=""
              currencySymbol={currencySymbol}
            />

            <button
              className={classes.removeBtn}
              onClick={() => handleRemoveRow(index)}
            >
              <ClearIcon fontSize="small" />
            </button>
          </div>
        );
      })}

      <div className={classes.addBtnContainer}>
        <button className={classes.addBtn} onClick={handleAddRow}>
          <AddCircleOutlineIcon
            htmlColor={MAIN_THEME.color.red}
            fontSize="medium"
          />
        </button>
      </div>
    </div>
  );
};

export default CoverSplit;
